import type { FC } from "react";
import { useRouter } from "next/router";
import styled from "styled-components";
import Icon from "@components/Icon";
import { helpsiteTrackCta } from "@helpers/helpsite-mixpanel";

type FreshchatWidget = { show: () => void; open: () => void };

/* Figma 644-15641: Frame 1183 - #F3F3F3, dashed border, row, icon + text */
const Container = styled.div`
	width: 100%;
	box-sizing: border-box;
	margin: 24px 0;
	padding: 10px;
	background-color: #f3f3f3;
	border: 1px dashed #000000;
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	gap: 10px;
`;

const IconWrapper = styled.div`
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
`;

const TextBlock = styled.p`
	font-family: var(--euclid-regular);
	font-size: 14px;
	font-weight: 400;
	line-height: 1.5;
	color: #000000;
	margin: 0;
	flex: 1;
	min-width: 0;
`;

const ContactButton = styled.button`
	background: none !important;
	border: none !important;
	border-width: 0 !important;
	box-shadow: none !important;
	outline: none !important;
	padding: 0 !important;
	color: #000000 !important;
	text-decoration: underline !important;
	font-family: var(--euclid-regular) !important;
	font-size: 14px !important;
	font-weight: 400 !important;
	line-height: 1.5 !important;
	cursor: pointer !important;
	transition: none !important;

	&:hover,
	&:active,
	&:focus {
		opacity: 1 !important;
		background: none !important;
		border: none !important;
		border-width: 0 !important;
		box-shadow: none !important;
		transform: none !important;
		transition: none !important;
	}
`;

const Bold = styled.span`
	font-weight: 700;
	font-size: inherit;
`;

const ContactSupportInfo: FC = () => {
	const router = useRouter();
	const pagePath = router.asPath.split("?")[0];

	const openChat = () => {
		helpsiteTrackCta({
			helpsite_section: "inline_contact_support",
			page_path: pagePath,
			label: "Contact our support team",
			cta_type: "freshchat_open",
		});
		if (typeof window !== "undefined") {
			const fcWidget = (window as Window & { fcWidget?: FreshchatWidget })
				.fcWidget;
			if (fcWidget) {
				fcWidget.show();
				fcWidget.open();
			}
		}
	};

	return (
		<Container>
			<IconWrapper>
				<Icon
					iconName="chat"
					iconSizes={{
						iconMobile: 40,
						iconTablet: 40,
						iconLaptop: 40,
						iconDesktop: 40,
						iconDesktopLarge: 40,
					}}
					alt=""
				/>
			</IconWrapper>
			<TextBlock>
				<ContactButton type="button" onClick={openChat}>
					Contact our support team:
				</ContactButton>{" "}
				We've got humans online to help you with this{" "}
				<Bold>Monday-Friday 8am-8pm</Bold> and{" "}
				<Bold>weekends/holidays 9am-6pm</Bold>, Sydney time.
			</TextBlock>
		</Container>
	);
};

export default ContactSupportInfo;
